import BuilderMain from "@components/main/BuilderMain";
import Sidebar from "@components/sidebar/Sidebar";
import LockBanner from "@components/lock/LockBanner";
import TranslatableText from "@components/translations/TranslatableText";
import BackButton from "@components/button/BackButton";
import { motion } from "framer-motion";
import { useAppParams, useAppSelector, useCustomTranslation } from "@app/hooks";
import { useMemo, useState } from "react";
import { MdArrowBack, MdCheckBoxOutlineBlank, MdSave } from "react-icons/md";
import { BiHelpCircle } from "react-icons/bi";
import {
  useLocation,
  useSearchParams,
  unstable_useBlocker as useBlocker,
} from "react-router-dom";
import { useHandleActivityState } from "@pages/activityBuilder/hooks/useHandleActivityState";
import { selectCurrentProject } from "@app/features/project/projectSlice";
import { selectIsMapSaveInProgress } from "@app/features/mapSaveStatus/mapSaveStatusSlice";
import { TranslationKey } from "@typedefs/modulesInterfaces";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@components/tooltip/Tooltip";
import BintactMap from "./BintactMap";
import useBintactMapCommunication from "./useBintactMapCommunication";
import BintactStatusMessage from "./BintactStatusMessage";
import ModuleButton from "@components/button/ModuleButton";
import ConfirmNavigation from "@components/confirmNavigation/ConfirmNavigation";
import ConfirmationModal from "@components/modal/ConfirmationModal";
import Skeleton from "react-loading-skeleton";

const BintactBuilder = () => {
  const translatedActivity = useCustomTranslation("main.activity");
  const translatedProject = useCustomTranslation("project.project");
  const { activity, isLoading, error } = useHandleActivityState();
  const { project, projectAreaDefined } = useAppSelector(selectCurrentProject);
  const { activityId, projectId } = useAppParams();
  const projectIdNum = useMemo(() => Number(projectId), [projectId]);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isSetBaselineModalOpen, setIsSetBaselineModalOpen] = useState(false);

  // Check if we're in baseline mode from query param
  const isBaselineMode = searchParams.get('mode') === 'baseline';

  // Extract all possible state properties
  const navigationState = location.state as {
    from?: string;
  } | null;

  const returnPath = navigationState?.from;

  const isMapSaveInProgress = useAppSelector((state) =>
    projectId ? selectIsMapSaveInProgress(state, projectId) : false,
  );
  const {
    iframeRef,
    requestSave,
    requestExit,
    requestSetBaseline,
    isSaving,
    mapUrl,
    hasUnsavedChanges,
    statusMessage,
  } = useBintactMapCommunication({
    initialState: !projectAreaDefined && project?.map_data ? {
      isFirstMapLoad: true,
      initialMapShape: JSON.parse(project?.map_data),
    } : undefined,
    isBaselineMode,
    returnPath,
  });

  // Block navigation if there are unsaved changes, unless save is in progress (user can leave)
  const shouldBlock = () =>
    (hasUnsavedChanges || isSaving) && !isMapSaveInProgress;
  const blocker = useBlocker(shouldBlock);

  // Handle set baseline with confirmation
  const handleSetBaselineClick = () => {
    setIsSetBaselineModalOpen(true);
  };

  const handleConfirmSetBaseline = () => {
    setIsSetBaselineModalOpen(false);
    requestSetBaseline();
  };

  const handleCancelSetBaseline = () => {
    setIsSetBaselineModalOpen(false);
  };

  const activityLabel = useMemo(() => {
    return activityId
      ? `${translatedActivity} ${activityId}.`
      : `${translatedProject} ${projectIdNum}.`;
  }, [activityId, translatedActivity, projectIdNum, translatedProject]);
  const projectLabel = useMemo(() => {
    return activityId ? activity?.name : project?.name;
  }, [activityId, activity?.name, project?.name]);

  const headerMessage: TranslationKey = useMemo(() => {
    if (!projectAreaDefined) {
      return "map.header_message_first_load";
    }
    if (isBaselineMode) {
      return "map.header_message_baseline";
    }
    if (activityId) {
      return "map.header_message_activity";
    }
    return "map.header_message_project";
  }, [projectAreaDefined, isBaselineMode, activityId]);

  if (error?.status === 403) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center f-column">
        <TranslatableText
          translationKey="user.not_allowed_project"
          className="ff-medium"
        />
        <BackButton
          to="/"
          backLabel="main.back_to_projects"
          style={{ maxWidth: 250, marginTop: 20 }}
        />
      </div>
    );
  }

  return (
    <motion.div className={`builder-container actions-close`}>
      <Sidebar />

      {/* Custom header using grid area "top" */}
      <div className="bintact-header-top">
        <div className="bintact-header-left">
          {isLoading ? (
            <Skeleton width={250} height={20} />
          ) : (
            <>
              <span className="fs-12 ff-normal-ext">{activityLabel}</span>
              <span className="fs-13 ff-bold-ext ms-2">{projectLabel}</span>
            </>
          )}
        </div>

        <div className="bintact-header-center">
          <TranslatableText
            translationKey={headerMessage}
            className="fs-10 ff-normal-cond pt-2"
            showTooltip
            style={{
              lineHeight: "1.4",
              textAlign: "right",
            }}
          />
        </div>

        <div className="bintact-header-right">
          <LockBanner />
        </div>
      </div>

      {/* Custom actions bar using grid area "maintabs" */}
      <div className="bintact-actions-bar">
        {!isBaselineMode && (
          <ModuleButton
            handleClick={requestSave}
            labelKey="main.save"
            icon={MdSave}
            isLoading={isSaving}
            className="h-100"
          />
        )}
        <ModuleButton
          handleClick={requestExit}
          labelKey="main.back"
          icon={MdArrowBack}
          className="h-100"
        />
        {!isBaselineMode && (
          <>
            <ModuleButton
              handleClick={handleSetBaselineClick}
              labelKey="main.set_as_baseline"
              icon={MdCheckBoxOutlineBlank}
              className="h-100"
            />
            <Tooltip placement="bottom">
              <TooltipTrigger asChild>
                <div
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <BiHelpCircle size={24} color="var(--icon)" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="msa-info-tooltip">
                <div style={{ maxWidth: "300px" }}>
                  <TranslatableText translationKey="map.baseline_description" />
                </div>
              </TooltipContent>
            </Tooltip>
          </>
        )}

        <BintactStatusMessage
          statusMessage={statusMessage}
          showContainer={false}
        />
      </div>

      <BuilderMain>
        <BintactMap iframeRef={iframeRef} mapUrl={mapUrl} />
      </BuilderMain>
      <ConfirmNavigation
        blocker={blocker}
        description="map.unsaved_changes_confirmation"
      />
      <ConfirmationModal
        open={isSetBaselineModalOpen}
        onCancel={handleCancelSetBaseline}
        onConfirm={handleConfirmSetBaseline}
        message={<TranslatableText translationKey="map.set_as_baseline_confirmation" />}
      />
    </motion.div>
  );
};

export default BintactBuilder;
